import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import PreviewBanner from '../../components/rpat/PreviewBanner';
import RetakeGuidance from '../../components/rpat/RetakeGuidance';
import { 
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Check,
  Play,
  Loader2,
  Award,
  Download,
  Trophy,
  Star,
  Calendar,
  User,
  Shield,
  Share2,
  Linkedin,
  Copy,
  ExternalLink,
  Link2,
  Lock,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Target,
  BookOpen,
  Briefcase,
  Users,
  MessageSquare
} from 'lucide-react';

const RPATFinalCertification = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [takingAssessment, setTakingAssessment] = useState(false);
  const [assessmentScore, setAssessmentScore] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  // Get the base URL for verification links
  const getVerificationUrl = (certNumber) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/certificate/verify/${certNumber}`;
  };

  const handleShareLinkedIn = () => {
    if (!certificate) return;
    
    const certUrl = getVerificationUrl(certificate.certificate_number);
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certUrl)}`;
    
    window.open(linkedInUrl, '_blank', 'width=600,height=600');
    toast.success('Opening LinkedIn to share your achievement!');
  };

  const handleCopyVerificationLink = () => {
    if (!certificate) return;
    
    const certUrl = getVerificationUrl(certificate.certificate_number);
    navigator.clipboard.writeText(certUrl);
    toast.success('Verification link copied to clipboard!');
  };

  const handleAddToLinkedInProfile = () => {
    if (!certificate) return;
    
    const issueDate = new Date(certificate.issued_at);
    const certUrl = getVerificationUrl(certificate.certificate_number);
    
    const linkedInAddUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent('TekWissen Certified Recruiter')}&organizationName=${encodeURIComponent('TekWissen Group')}&issueYear=${issueDate.getFullYear()}&issueMonth=${issueDate.getMonth() + 1}&certUrl=${encodeURIComponent(certUrl)}&certId=${encodeURIComponent(certificate.certificate_number)}`;
    
    window.open(linkedInAddUrl, '_blank', 'width=800,height=700');
    toast.success('Opening LinkedIn to add certification to your profile!');
  };

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certification/status');
      setStatus(res.data);
      
      const isAdmin = user?.role === 'admin';
      
      if (!isAdmin && res.data.phases?.phase_7?.status !== 'completed') {
        setIsPreviewMode(true);
      }
      
      if (res.data.phases?.phase_8?.certificate_id) {
        const certRes = await api.get(`/rpat/certification/certificate/${res.data.phases.phase_8.certificate_id}`);
        setCertificate(certRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch status:', err);
      toast.error('Failed to load certification status');
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  const fetchScoreBreakdown = useCallback(async () => {
    try {
      setLoadingBreakdown(true);
      const res = await api.get('/rpat/certification/score-breakdown');
      setScoreBreakdown(res.data);
    } catch (err) {
      console.error('Failed to fetch score breakdown:', err);
    } finally {
      setLoadingBreakdown(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    fetchScoreBreakdown();
  }, [fetchStatus, fetchScoreBreakdown]);

  const handleStartAssessment = async () => {
    setTakingAssessment(true);
    
    try {
      const res = await api.post('/rpat/certification/final-assessment', {});
      setAssessmentScore(res.data.score);
      
      if (res.data.passed) {
        toast.success('Congratulations! You passed the final assessment!');
        setCertificate(res.data.certificate);
      } else {
        toast.error(`Score: ${res.data.score}%. You need 70% to pass.`);
        // Refresh breakdown to show updated guidance
        fetchScoreBreakdown();
      }
    } catch (err) {
      console.error('Failed to submit assessment:', err);
      toast.error(err.response?.data?.detail || 'Failed to process assessment');
      setTakingAssessment(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificateRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: '#ffffff'
      });
      
      const link = document.createElement('a');
      link.download = `TekWissen_Certificate_${certificate?.certificate_number || 'cert'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Certificate downloaded!');
    } catch (err) {
      console.error('Failed to download:', err);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  // Phase icons for breakdown display
  const phaseIcons = {
    phase_1_4: BookOpen,
    phase_5: Briefcase,
    phase_6: Users,
    phase_7: MessageSquare
  };

  // Phase navigation routes
  const phaseRoutes = {
    phase_1_4: '/rpat',
    phase_5: '/rpat/certification/job-mastery',
    phase_6: '/rpat/certification/sourcing-labs',
    phase_7: '/rpat/certification/roleplay'
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/rpat')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to R-PAT
          </Button>
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <PreviewBanner 
            message="Complete Phase 7 (Role Play Lab) to unlock the final certification."
            actionLabel="Go to Phase 7"
            onAction={() => navigate('/rpat/certification/roleplay')}
          />
        )}

        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="inline-flex p-4 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 mb-4">
            <GraduationCap className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold">Phase 8: Final Certification</h1>
          <p className="text-muted-foreground mt-2">
            Review your progress and earn your TekWissen Certified Recruiter certificate
          </p>
          <Badge className="mt-3 bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200">
            Advanced Certification (Phase 5-8)
          </Badge>
        </div>

        {/* Score Breakdown Section - Always show this */}
        {scoreBreakdown && (
          <Card className="max-w-3xl mx-auto" data-testid="score-breakdown-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-cyan-500" />
                Your Score Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Overall Score Display */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                <div>
                  <p className="text-sm text-muted-foreground">Overall Score</p>
                  <p
                    data-testid="overall-score-value"
                    className={`text-4xl font-bold ${
                      scoreBreakdown.is_passing ? 'text-emerald-500' : 'text-red-500'
                    }`}
                  >
                    {scoreBreakdown.overall_score}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Passing Threshold</p>
                  <p className="text-2xl font-semibold" data-testid="passing-threshold-value">{scoreBreakdown.passing_threshold}%</p>
                </div>
                <div className={`p-3 rounded-full ${
                  scoreBreakdown.is_passing 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-red-100 text-red-600'
                }`}>
                  {scoreBreakdown.is_passing ? (
                    <Check className="h-8 w-8" />
                  ) : (
                    <AlertTriangle className="h-8 w-8" />
                  )}
                </div>
              </div>

              <Separator />

              {/* Individual Phase Scores */}
              <div className="space-y-4">
                <h4 className="font-medium">Phase-by-Phase Performance</h4>
                
                {Object.entries(scoreBreakdown.breakdown).map(([phaseKey, phase]) => {
                  const PhaseIcon = phaseIcons[phaseKey] || Target;
                  const isWeak = phase.score !== null && phase.score < scoreBreakdown.passing_threshold;
                  const isComplete = phase.status === 'completed' || phase.score !== null;
                  
                  return (
                    <div 
                      key={phaseKey}
                      data-testid={`phase-breakdown-${phaseKey}`}
                      className={`p-4 rounded-lg border ${
                        isWeak 
                          ? 'border-red-200 bg-red-50 dark:bg-red-950/20' 
                          : isComplete
                            ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20'
                            : 'border-gray-200 bg-gray-50 dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            isWeak 
                              ? 'bg-red-100 text-red-600' 
                              : isComplete
                                ? 'bg-emerald-100 text-emerald-600'
                                : 'bg-gray-200 text-gray-500'
                          }`}>
                            <PhaseIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{phase.label}</p>
                            <p className="text-sm text-muted-foreground">
                              {phase.count > 0 
                                ? `${phase.count}${phase.total ? `/${phase.total}` : ''} completed`
                                : 'Not started'
                              }
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {phase.score !== null ? (
                            <div className="text-right">
                              <p
                                data-testid={`phase-score-${phaseKey}`}
                                className={`text-2xl font-bold ${
                                  isWeak ? 'text-red-500' : 'text-emerald-500'
                                }`}
                              >
                                {phase.score}%
                              </p>
                              {isWeak && (
                                <p className="text-xs text-red-500">
                                  Need {scoreBreakdown.passing_threshold}%+
                                </p>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">
                              No data
                            </Badge>
                          )}
                          
                          {/* Retake button for weak phases */}
                          {isWeak && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`retake-${phaseKey}-button`}
                              className="border-red-300 text-red-600 hover:bg-red-100"
                              onClick={() => navigate(phaseRoutes[phaseKey])}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Retake
                            </Button>
                          )}
                          
                          {/* CTA for Phase 1-4 with no data */}
                          {phaseKey === 'phase_1_4' && phase.score === null && (
                            <Button
                              size="sm"
                              variant="default"
                              data-testid="phase-1-4-cta-button"
                              className="bg-cyan-500 hover:bg-cyan-600"
                              onClick={() => navigate('/rpat')}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Take Quizzes
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Phase 1-4 CTA Banner when no data */}
                      {phaseKey === 'phase_1_4' && phase.score === null && (
                        <div className="mt-3 p-3 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg border border-cyan-200">
                          <p className="text-sm text-cyan-800 dark:text-cyan-200">
                            <strong>Boost your score!</strong> Complete the Phase 1-4 knowledge quizzes to add 25% to your weighted average.
                          </p>
                        </div>
                      )}
                      
                      {/* Show individual scores if available */}
                      {phase.individual_scores && phase.individual_scores.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-dashed">
                          <p className="text-xs text-muted-foreground mb-2">Individual scores:</p>
                          <div className="flex flex-wrap gap-2">
                            {phase.individual_scores.map((score, idx) => (
                              <Badge 
                                key={idx}
                                variant="outline"
                                className={score < scoreBreakdown.passing_threshold 
                                  ? 'bg-red-50 text-red-600 border-red-200'
                                  : 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                }
                              >
                                {score}%
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Improvement Guidance */}
              {scoreBreakdown.weak_phases.length > 0 && (
                <>
                  <Separator />
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200" data-testid="improvement-recommendations">
                    <div className="flex items-start gap-3">
                      <TrendingUp className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-800 dark:text-amber-300">
                          Improvement Recommendations
                        </h4>
                        <ul className="mt-2 space-y-2">
                          {scoreBreakdown.improvement_guidance.map((guidance, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <ChevronRight className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-medium">{guidance.action}</span>
                                <span className="text-muted-foreground ml-1">— {guidance.tip}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Retake Guidance Section - Show when not passing */}
        {scoreBreakdown && !scoreBreakdown.is_passing && !certificate && (
          <div className="max-w-3xl mx-auto mt-6">
            <RetakeGuidance onRetakeComplete={fetchScoreBreakdown} />
          </div>
        )}

        {/* Assessment or Certificate Section */}
        <div className="max-w-3xl mx-auto mt-8">
          {!certificate ? (
            <div>
              {!takingAssessment ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Trophy className="h-16 w-16 mx-auto text-amber-500 mb-4" />
                    <h3 className="text-2xl font-semibold mb-2">
                      {scoreBreakdown?.is_passing 
                        ? 'Ready for Your Certificate!' 
                        : 'Review Your Scores Above'}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {scoreBreakdown?.is_passing 
                        ? `Your overall score of ${scoreBreakdown.overall_score}% qualifies for certification! Click below to generate your certificate.`
                        : `Your current score is ${scoreBreakdown?.overall_score || 0}%. You need 70%+ to earn your certificate. Improve the phases highlighted in red above.`
                      }
                    </p>
                    
                    {scoreBreakdown?.is_passing ? (
                      <Button
                        size="lg"
                        data-testid="generate-certificate-button"
                        className="bg-gradient-to-r from-cyan-500 to-blue-600"
                        onClick={handleStartAssessment}
                      >
                        <Award className="mr-2 h-5 w-5" />
                        Generate My Certificate
                      </Button>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2 justify-center">
                          {scoreBreakdown?.weak_phases.map((wp) => (
                            <Button
                              key={wp.phase}
                              variant="outline"
                              data-testid={`improve-${wp.phase}-button`}
                              className="border-red-300 text-red-600 hover:bg-red-100"
                              onClick={() => navigate(phaseRoutes[wp.phase])}
                            >
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Improve {wp.label.split(' ')[0]} {wp.label.split(' ')[1]}
                            </Button>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Gap to passing: {(scoreBreakdown?.passing_threshold - scoreBreakdown?.overall_score).toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="text-center py-12">
                  <CardContent>
                    {!assessmentScore ? (
                      <div>
                        <Loader2 className="h-16 w-16 mx-auto animate-spin text-cyan-600 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Generating Your Certificate...</h3>
                        <p className="text-muted-foreground">
                          Calculating your final score across all modules
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className={`text-6xl font-bold mb-4 ${
                          assessmentScore >= 70 ? 'text-emerald-500' : 'text-red-500'
                        }`}>
                          {assessmentScore}%
                        </div>
                        <h3 className="text-xl font-semibold mb-2">
                          {assessmentScore >= 70 ? 'Congratulations!' : 'Keep Improving!'}
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          {assessmentScore >= 70 
                            ? 'You passed! Your certificate has been issued.'
                            : `You need 70% to pass. Review the score breakdown above to see where to improve.`
                          }
                        </p>
                        {assessmentScore >= 70 ? (
                          <Button
                            size="lg"
                            className="bg-gradient-to-r from-emerald-500 to-green-600"
                            onClick={() => {
                              setTakingAssessment(false);
                              fetchStatus();
                            }}
                          >
                            <Award className="mr-2 h-5 w-5" />
                            View Certificate
                          </Button>
                        ) : (
                          <Button
                            size="lg"
                            variant="outline"
                            onClick={() => {
                              setTakingAssessment(false);
                              setAssessmentScore(null);
                            }}
                          >
                            <ChevronLeft className="mr-2 h-5 w-5" />
                            Back to Score Breakdown
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Certificate Display */}
              <div 
                ref={certificateRef}
                className="bg-white border-4 border-double border-cyan-600 rounded-lg p-8 shadow-xl"
                style={{ 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 50%, #ffffff 100%)'
                }}
              >
                {/* Certificate Header */}
                <div className="text-center border-b-2 border-cyan-200 pb-6 mb-6">
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      <Shield className="h-20 w-20 text-cyan-600" />
                      <Award className="h-10 w-10 text-amber-500 absolute -bottom-1 -right-1" />
                    </div>
                  </div>
                  <h1 className="text-4xl font-serif font-bold text-gray-800 mb-2">
                    CERTIFICATE OF ACHIEVEMENT
                  </h1>
                  <p className="text-cyan-600 font-medium tracking-widest">
                    TEKWISSEN - CERTIFIED RECRUITER
                  </p>
                </div>

                {/* Certificate Body */}
                <div className="text-center mb-8">
                  <p className="text-gray-600 mb-4">This is to certify that</p>
                  <h2 className="text-3xl font-serif font-bold text-gray-800 mb-4 border-b-2 border-amber-400 inline-block pb-2 px-8">
                    {certificate.user_name}
                  </h2>
                  <p className="text-gray-600 mb-4">
                    has successfully completed the
                  </p>
                  <h3 className="text-2xl font-semibold text-cyan-700 mb-2">
                    R-PAT: Recruitment Process Autonomous Training
                  </h3>
                  <p className="text-gray-600 mb-2">
                    with specialization in
                  </p>
                  <Badge className="text-lg px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600">
                    {certificate.specialization}
                  </Badge>
                </div>

                {/* Achievement Details */}
                <div className="grid grid-cols-3 gap-4 mb-8 text-center">
                  <div className="p-4 bg-cyan-50 rounded-lg">
                    <Star className="h-6 w-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Score</p>
                    <p className="text-xl font-bold text-cyan-700">{certificate.score}%</p>
                  </div>
                  <div className="p-4 bg-cyan-50 rounded-lg">
                    <Calendar className="h-6 w-6 text-cyan-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Issued</p>
                    <p className="text-xl font-bold text-cyan-700">
                      {new Date(certificate.issued_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="p-4 bg-cyan-50 rounded-lg">
                    <Shield className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Certificate ID</p>
                    <p className="text-sm font-bold text-cyan-700 break-all">{certificate.certificate_number}</p>
                  </div>
                </div>

                {/* Verification Link */}
                <div className="text-center border-t-2 border-cyan-200 pt-4">
                  <p className="text-xs text-gray-500">
                    Verify this certificate at: {getVerificationUrl(certificate.certificate_number)}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600"
                  onClick={handleDownloadCertificate}
                  disabled={downloading}
                >
                  {downloading ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-5 w-5" />
                  )}
                  Download Certificate
                </Button>
                
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setShowShareOptions(!showShareOptions)}
                >
                  <Share2 className="mr-2 h-5 w-5" />
                  Share
                </Button>
              </div>

              {/* Share Options */}
              {showShareOptions && (
                <Card className="p-4">
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={handleShareLinkedIn}
                    >
                      <Linkedin className="h-5 w-5 text-blue-600" />
                      Share on LinkedIn
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={handleAddToLinkedInProfile}
                    >
                      <ExternalLink className="h-5 w-5 text-blue-600" />
                      Add to LinkedIn Profile
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={handleCopyVerificationLink}
                    >
                      <Copy className="h-5 w-5" />
                      Copy Verification Link
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default RPATFinalCertification;
